import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BunnyLoader from '../components/ui/BunnyLoader';
import { Button } from '../components/ui/button';
import { hirelaneApi } from '../services/api';
import './HirelaneDashboard.css';
import './HirelaneJobCreatePage.css';

function errMessage(e) {
  const data = e?.response?.data;
  if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors
      .map((err) => {
        const field = err.path || err.param || 'field';
        const msg = err.msg || 'invalid';
        return `${field}: ${msg}`;
      })
      .join('; ');
  }
  return data?.error || e?.message || 'Something went wrong';
}

function statusBadgeClass(status) {
  if (status === 'Open') return 'hirelane-dash__status hirelane-dash__status--open';
  if (status === 'Closed') return 'hirelane-dash__status hirelane-dash__status--closed';
  return 'hirelane-dash__status hirelane-dash__status--draft';
}

function jobToFormState(job) {
  if (!job) return null;
  return {
    title: job.title ?? '',
    department: job.department ?? '',
    experience_min:
      job.experience_min != null && job.experience_min !== '' ? String(job.experience_min) : '',
    experience_max:
      job.experience_max != null && job.experience_max !== '' ? String(job.experience_max) : '',
    location: job.location ?? '',
    employment_type: job.employment_type ?? '',
    key_skills: job.key_skills ?? '',
    responsibilities: job.responsibilities ?? '',
    additional_notes: job.additional_notes ?? '',
    final_jd: job.final_jd ?? '',
    status: job.status && ['Draft', 'Open', 'Closed'].includes(job.status) ? job.status : 'Draft'
  };
}

export default function HirelaneJobCreatePage() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const isEdit = Boolean(jobId);

  const [loadingJob, setLoadingJob] = useState(!!isEdit);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);

  /** 'form' = user fills fields directly; 'describe' = free-text intent first */
  const [approach, setApproach] = useState('form');
  const [hiringIntent, setHiringIntent] = useState('');

  const [form, setForm] = useState({
    title: '',
    department: '',
    experience_min: '',
    experience_max: '',
    location: '',
    employment_type: '',
    key_skills: '',
    responsibilities: '',
    additional_notes: '',
    final_jd: '',
    status: 'Draft'
  });

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingJob(true);
        setError(null);
        const job = await hirelaneApi.getJob(jobId);
        if (cancelled) return;
        const next = jobToFormState(job);
        if (next) setForm((f) => ({ ...f, ...next }));
      } catch (e) {
        if (!cancelled) setError(errMessage(e));
      } finally {
        if (!cancelled) setLoadingJob(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const canGenerate = useMemo(() => form.title.trim().length > 0, [form.title]);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const applyParsedFields = (fields) => {
    setForm((f) => ({
      ...f,
      title: fields.title ?? '',
      department: fields.department ?? '',
      experience_min:
        fields.experience_min != null && fields.experience_min !== ''
          ? String(fields.experience_min)
          : '',
      experience_max:
        fields.experience_max != null && fields.experience_max !== ''
          ? String(fields.experience_max)
          : '',
      location: fields.location ?? '',
      employment_type: fields.employment_type ?? '',
      key_skills: fields.key_skills ?? '',
      responsibilities: fields.responsibilities ?? '',
      additional_notes: fields.additional_notes ?? ''
    }));
  };

  const fillFieldsFromIntent = async () => {
    const text = hiringIntent.trim();
    if (text.length < 10) {
      setError('Please write at least a sentence about what you are looking for.');
      return;
    }
    try {
      setParsing(true);
      setError(null);
      const data = await hirelaneApi.parseJobIntent(text);
      if (data?.fields) applyParsedFields(data.fields);
      else setError('Unexpected response from Cony');
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setParsing(false);
    }
  };

  const generate = async () => {
    if (!canGenerate) return;
    try {
      setGenerating(true);
      setError(null);
      const payload = {
        ...form,
        experience_min: form.experience_min ? Number(form.experience_min) : null,
        experience_max: form.experience_max ? Number(form.experience_max) : null
      };
      const result = await hirelaneApi.generateJobDescription(payload);
      setForm((f) => ({
        ...f,
        final_jd: result?.ai_generated_jd || f.final_jd
      }));
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        title: form.title,
        department: form.department || null,
        experience_min: form.experience_min ? Number(form.experience_min) : null,
        experience_max: form.experience_max ? Number(form.experience_max) : null,
        location: form.location || null,
        employment_type: form.employment_type || null,
        key_skills: form.key_skills || null,
        responsibilities: form.responsibilities || null,
        final_jd: form.final_jd || null,
        status: isEdit ? form.status || 'Draft' : 'Draft'
      };
      if (isEdit) {
        await hirelaneApi.updateJob(jobId, payload);
        navigate(`/hirelane/jobs/${jobId}`);
      } else {
        const job = await hirelaneApi.createJob(payload);
        navigate(`/hirelane/jobs/${job.id}`);
      }
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && loadingJob) return <BunnyLoader />;

  const subtitleCreate =
    'Choose how to start: fill the fields yourself, or describe what you need in plain language—Cony will suggest values you can edit before generating or saving the job description.';
  const subtitleEdit =
    'Update posting fields and the job description. Saving returns you to the pipeline with your latest brief.';

  return (
    <div className="hirelane-dash">
      <header className="hirelane-dash__hero">
        <div className="hirelane-dash__heroInner">
          <div>
            <p className="hirelane-dash__kicker">Hirelane</p>
            <div className="hirelane-job-form__titleRow">
              <h1 className="hirelane-dash__title">{isEdit ? 'Edit job' : 'Create job'}</h1>
              {isEdit && form.status ? (
                <span className={statusBadgeClass(form.status)}>{form.status}</span>
              ) : null}
            </div>
            <p className="hirelane-dash__subtitle">{isEdit ? subtitleEdit : subtitleCreate}</p>
          </div>
          <div className="hirelane-job-form__heroActions">
            <Button
              variant="secondary"
              onClick={() => (isEdit ? navigate(`/hirelane/jobs/${jobId}`) : navigate('/hirelane'))}
              disabled={saving || generating || parsing}
            >
              {isEdit ? 'Cancel' : 'Back'}
            </Button>
            <Button onClick={save} disabled={saving || generating || parsing || !form.title.trim()}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save job'}
            </Button>
          </div>
        </div>
      </header>

      <div className="hirelane-job-form__stack">
        {error ? <div className="hirelane-dash__alert">{error}</div> : null}

        {!isEdit ? (
          <section className="hirelane-job-form__panel">
            <p className="hirelane-job-form__lede">
              Pick a starting point below. You can switch anytime before saving.
            </p>
            <div className="hirelane-job-form__segmented">
              <Button
                variant={approach === 'form' ? 'primary' : 'secondary'}
                onClick={() => setApproach('form')}
                disabled={parsing || generating || saving}
              >
                Fill fields myself
              </Button>
              <Button
                variant={approach === 'describe' ? 'primary' : 'secondary'}
                onClick={() => setApproach('describe')}
                disabled={parsing || generating || saving}
              >
                Describe what I need
              </Button>
            </div>
          </section>
        ) : null}

        {!isEdit && approach === 'describe' ? (
          <section className="hirelane-job-form__panel">
            <h3 className="hirelane-job-form__intentTitle">What are you looking for?</h3>
            <p className="hirelane-job-form__intentCopy">
              Include role, seniority, tech stack, location or remote preference, team context, and anything else that
              matters. Cony will fill the form below—you can adjust before generating the JD.
            </p>
            <textarea
              className="hirelane-job-form__textarea"
              value={hiringIntent}
              onChange={(e) => setHiringIntent(e.target.value)}
              placeholder="Example: We need someone to lead our React frontend for a B2B SaaS product. 5+ years, strong TypeScript, has mentored juniors. Hybrid in Bangalore, full-time."
              rows={8}
            />
            <div className="hirelane-job-form__intentActions">
              <Button variant="secondary" onClick={fillFieldsFromIntent} disabled={parsing || generating || saving}>
                {parsing ? 'Analyzing…' : 'Fill fields with Cony'}
              </Button>
            </div>
          </section>
        ) : null}

        <section className="hirelane-job-form__panel">
          <h2 className="hirelane-job-form__sectionTitle">Job details</h2>
          <div className="hirelane-job-form__grid">
            <div className="hirelane-job-form__field">
              <span className="hirelane-job-form__label">Job title</span>
              <input
                className="hirelane-job-form__input"
                value={form.title}
                onChange={onChange('title')}
                placeholder="Role title"
                autoComplete="off"
              />
            </div>
            <div className="hirelane-job-form__field">
              <span className="hirelane-job-form__label">Department / team</span>
              <input
                className="hirelane-job-form__input"
                value={form.department}
                onChange={onChange('department')}
                placeholder="e.g. Engineering"
                autoComplete="off"
              />
            </div>
            <div className="hirelane-job-form__field">
              <span className="hirelane-job-form__label">Location</span>
              <input
                className="hirelane-job-form__input"
                value={form.location}
                onChange={onChange('location')}
                placeholder="City or remote"
                autoComplete="off"
              />
            </div>
            <div className="hirelane-job-form__field">
              <span className="hirelane-job-form__label">Employment type</span>
              <input
                className="hirelane-job-form__input"
                value={form.employment_type}
                onChange={onChange('employment_type')}
                placeholder="e.g. Full-time"
                autoComplete="off"
              />
            </div>
            <div className="hirelane-job-form__field">
              <span className="hirelane-job-form__label">Experience min (years)</span>
              <input
                className="hirelane-job-form__input"
                value={form.experience_min}
                onChange={onChange('experience_min')}
                inputMode="numeric"
                placeholder="Min"
                autoComplete="off"
              />
            </div>
            <div className="hirelane-job-form__field">
              <span className="hirelane-job-form__label">Experience max (years)</span>
              <input
                className="hirelane-job-form__input"
                value={form.experience_max}
                onChange={onChange('experience_max')}
                inputMode="numeric"
                placeholder="Max"
                autoComplete="off"
              />
            </div>
            {isEdit ? (
              <div className="hirelane-job-form__field">
                <span className="hirelane-job-form__label">Job status</span>
                <select className="hirelane-job-form__select" value={form.status} onChange={onChange('status')}>
                  <option value="Draft">Draft</option>
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            ) : null}
          </div>

          <div className="hirelane-job-form__field hirelane-job-form__field--stack">
            <span className="hirelane-job-form__label">Key skills</span>
            <textarea
              className="hirelane-job-form__textarea"
              value={form.key_skills}
              onChange={onChange('key_skills')}
              placeholder="Must-have skills and technologies"
              rows={3}
            />
          </div>
          <div className="hirelane-job-form__field hirelane-job-form__field--stack">
            <span className="hirelane-job-form__label">Responsibilities</span>
            <textarea
              className="hirelane-job-form__textarea"
              value={form.responsibilities}
              onChange={onChange('responsibilities')}
              placeholder="What they will own day to day"
              rows={4}
            />
          </div>
          <div className="hirelane-job-form__field hirelane-job-form__field--stack">
            <span className="hirelane-job-form__label">Additional notes</span>
            <textarea
              className="hirelane-job-form__textarea"
              value={form.additional_notes}
              onChange={onChange('additional_notes')}
              placeholder="Internal notes (optional)"
              rows={3}
            />
          </div>
        </section>

        <section className="hirelane-job-form__panel">
          <div className="hirelane-job-form__jdHeader">
            <h2 className="hirelane-job-form__sectionTitle">Job description</h2>
            <Button variant="secondary" onClick={generate} disabled={generating || saving || parsing || !canGenerate}>
              {generating ? 'Generating…' : 'Generate with Cony'}
            </Button>
          </div>
          <p className="hirelane-job-form__hint">
            Uses the job details above. Edit fields first if anything looks off, then generate or paste your own JD.
          </p>
          <textarea
            className="hirelane-job-form__textarea hirelane-job-form__textarea--lg"
            value={form.final_jd}
            onChange={onChange('final_jd')}
            placeholder="Final job description (editable). Generate with Cony or paste your own."
            rows={14}
          />
        </section>
      </div>
    </div>
  );
}
